import { FormattedMessage, useLocale } from "@/i18n/i18n-client";
import {
  ProductBasicInfoFragment,
  ProductInfoFragment,
} from "../../../../generated/graphql";
import { useStore } from "@/lib/storeData/StoreContext";
import { BuildStateCategory } from "../../../../templates/types";
import { CustomButton } from "../../components/Button/CustomButton";
import useBreakPoints from "../../../../shared/utils/useBreakPoints";
import { useCartData } from "../../../../lib/cartData/useCartData";
import { useState } from "react";
import HandleBuyNow from "../../../../shared/utils/handleBuyNow";
import BuyNowConfirmModal from "../../../../shared/globals/UiElements/BuyNowConfirmModal";
import { ButtonProps } from "../Button/Button";
import CentralizedModal from "@/components/Centralizedlogin/CentralizedModal";
import { AuthMode } from "@/generated/graphql";
import { useAuth } from "@/lib/Authentication/hooks/useAuth";
import { useRouter } from "@/i18n/i18n-navigation";
import { getPaymentLink } from "@/shared/utils/shinglePageCheckoutFunctions";

interface BuyNowButtonProps extends ButtonProps {
  product:
    | ProductBasicInfoFragment
    | (Omit<ProductInfoFragment, "categories"> & {
        categories: BuildStateCategory[];
      });
  isDisabled?: boolean;
  quantity?: number;
  buildState?: BuildStateCategory[] | undefined;
  borderRadius?: number;
}

const BuyNowButton = ({
  product,
  isDisabled,
  quantity = 1,
  buildState,
  borderRadius,
  ...props
}: BuyNowButtonProps) => {
  const { appearance, authMode, handle, WuiltPayState } = useStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCentralizedModalOpen, setIsCentralizedModalOpen] = useState(false);
  const { cart } = useCartData();
  const { user } = useAuth();
  const { code: localeCode } = useLocale();
  const router = useRouter();
  const token = user?.token;
  const { isTablet } = useBreakPoints();

  const handleBuyNow = HandleBuyNow({
    buildState,
    product,
    quantity,
  });

  const handleBuyNowClick = async () => {
    if (cart?.items?.length) {
      setIsModalOpen(true);
      return;
    }
    const newCart = await handleBuyNow();
    const cartId = cart?.id || newCart?.id!;
    if (authMode === AuthMode.Modern && !token) {
      setIsCentralizedModalOpen(true);
    } else {
      router.push(
        WuiltPayState?.enabled
          ? getPaymentLink(handle, cartId, user?.token, localeCode)
          : `/checkout/${cartId}`
      );
      setIsModalOpen(false);
    }
  };
  return (
    <>
      <CustomButton
        data-test="button-buy-now"
        fullWidth
        isDisabled={isDisabled}
        customDefault={{
          backgroundColor:
            appearance?.productDisplay?.buyNowButton?.backgroundColor!,
          borderColor: appearance?.productDisplay?.buyNowButton?.borderColor!,
          color: appearance?.productDisplay?.buyNowButton?.fontColor!,
        }}
        borderRadius={borderRadius}
        onClick={handleBuyNowClick}
        className="h-full"
        fontSize={isTablet ? 14 : 16}
        {...props}
      >
        <FormattedMessage defaultMessage="Buy Now" id="lCILNz" />
      </CustomButton>
      <BuyNowConfirmModal
        onClose={() => setIsModalOpen(false)}
        productName={product.title}
        show={isModalOpen}
        handleBuyNow={handleBuyNow}
        loading={cart?.isLoading}
      />
      {authMode === AuthMode.Modern && (
        <CentralizedModal
          isOpen={isCentralizedModalOpen}
          onClose={() => setIsCentralizedModalOpen(false)}
          onComplete={() => setIsCentralizedModalOpen(false)}
          onAuthSuccess={() => {
            // After successful authentication, navigate to checkout
            setIsCentralizedModalOpen(false);
            router.push(
              WuiltPayState?.enabled
                ? getPaymentLink(handle, cart?.id, user?.token, localeCode)
                : `/checkout/${cart?.id}`
            );
          }}
        />
      )}
    </>
  );
};

export default BuyNowButton;
